import { FormBuilder, FormControl, UntypedFormGroup, ValidatorFn, Validators } from '@angular/forms';

import { isString } from '@summit/core';
import { DisplaySchema, DisplayType, SummitIdObject, ViewField } from '@summit/models';

export class FormFunctions {
  public static BuildFormGroup<T extends SummitIdObject>(
    formBuilder: FormBuilder,
    formGroupParameters: any,
    parentNode: Array<ViewField<T>> | DisplaySchema<T> | T | Partial<T>,
    component: any,
    hiddenFields: (keyof T)[] = []
  ) {
    let entries: ViewField<T>[];
    if ('refColumnList' in parentNode) {
      entries = (parentNode as DisplaySchema<T>).getFormAttributeList() as ViewField<T>[];
    } else {
      // console.log('parentNode:', parentNode)
      if (Array.isArray(parentNode)) {
        entries = parentNode.filter((entry) => entry.formField || entry.children);
      } else {
        entries = Object.keys(parentNode as any)
          .filter((key) => (parentNode[key] as any).formField || (parentNode[key] as any).children)
          .map((key) => parentNode[key]) as ViewField<T>[];
      }
    }
    entries.forEach((entry) => {
      if (entry.formField) {
        //console.log(`Adding Control ${entry.propertyName}`, entry, formBuilder);
        this.addFormControlConfig(formBuilder, formGroupParameters, entry, hiddenFields);
      } else if (entry.children) {
        const childFormGroupParameters = new Object();
        this.BuildFormGroup(formBuilder, childFormGroupParameters, entry.children, component);
        // console.log(`Adding Group ${entry.propertyName} with `, childFormGroupParameters);
        // console.log(`Produced`, childFormGroupParameters);
        formGroupParameters[entry.propertyName] = formBuilder.group(childFormGroupParameters);
        component[entry.propertyName] = formGroupParameters[entry.propertyName];
      }
    });
  }
  private static addFormControlConfig<T>(
    formBuilder: FormBuilder,
    formGroupParameters: any,
    entry: ViewField<T>,
    hiddenFields: (keyof T)[]
  ) {
    // Build Validators Array
    const validatorsArray = new Array<ValidatorFn>();
    //console.log(`Adding control ${entry.propertyName} index = ${hiddenFields.indexOf(entry.propertyName)}`);
    if (entry.validationMessages) {
      entry.validationMessages.forEach((validation) => {
        switch (validation.type) {
          case 'required':
            validatorsArray.push(Validators.required);
            break;
          case 'max':
            validatorsArray.push(Validators.max(validation.parameter as number));
            break;
          case 'min':
            validatorsArray.push(Validators.min(validation.parameter as number));
            break;
          case 'minlength':
            validatorsArray.push(Validators.minLength(validation.parameter as number));
            break;
          case 'nullValidator':
            validatorsArray.push(Validators.nullValidator);
            break;
          case 'pattern':
            //console.log(`Adding pattern to ${entry.propertyName}`);
            validatorsArray.push(Validators.pattern(validation.parameter as RegExp));
            break;
          default:
            if (!validation.custom && !validation.type?.startsWith('custom')) {
              console.error(`Unsupported validation Type ${validation.type} on ${validation.parameter}`);
            }
            break;
        }
      });
    }
    switch (entry.displayType) {
      case DisplayType.Boolean:
        formGroupParameters[entry.propertyName] = new FormControl<boolean | null>(
          null,
          this.addValidators<T>(entry.propertyName, hiddenFields, validatorsArray)
        );
        break;
      case DisplayType.Enum:
        formGroupParameters[entry.propertyName] = new FormControl<number | null>(
          null,
          this.addValidators<T>(entry.propertyName, hiddenFields, validatorsArray)
        );
        break;
      default:
        formGroupParameters[entry.propertyName] = new FormControl<string>(
          '',
          this.addValidators<T>(entry.propertyName, hiddenFields, validatorsArray)
        );
    }
  }

  private static addValidators<T>(
    propertyName: keyof T,
    hiddenFields: (keyof T)[],
    validatorsArray: ValidatorFn[]
  ): ValidatorFn | null {
    if (validatorsArray.length > 0 && hiddenFields.indexOf(propertyName) === -1) {
      return Validators.compose(validatorsArray);
    }
    return null;
  }

  public static FindFormControl(form: UntypedFormGroup, name: string): any {
    const separatorIndex = name.indexOf('.');
    if (separatorIndex >= 0) {
      const nameArray = name.split('.');
      const formGroupName = nameArray.shift();
      const subFormGroup = <UntypedFormGroup>(<unknown>form.get(formGroupName as any));
      if (subFormGroup) {
        return this.FindFormControl(subFormGroup, nameArray.join('.'));
      }
      return null;
    }
    return form.get(name);
  }

  public static JsonCleanUp(this: any, _key: any, value: any): any {
    if (isString(value)) {
      value = value.trim();
      if (value.length === 0) {
        return null;
      }
    }
    return value;
  }
}
